import React, { useState, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';


interface MaintenancePageProps {
  onRetry: () => void;
  isChecking: boolean;
  retryCount: number;
  lastChecked: Date | null;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({
  onRetry,
  isChecking,
  retryCount,
  lastChecked,
}) => {
  const [timeUntilNextCheck, setTimeUntilNextCheck] = useState(30);

  useEffect(() => {
    if (!lastChecked) return;

    const interval = setInterval(() => {
      const now = new Date();
      const timeSinceCheck = Math.floor((now.getTime() - lastChecked.getTime()) / 1000);
      const remaining = Math.max(0, 30 - timeSinceCheck);
      setTimeUntilNextCheck(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [lastChecked]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-orange-500/20 rounded-full flex items-center justify-center mb-4">
            {isChecking ? (
              <RefreshCw className="w-12 h-12 text-orange-400 animate-spin" />
            ) : (
              <WifiOff className="w-12 h-12 text-orange-400" />
            )}
          </div>
        </div>

       
        <h1 className="text-3xl font-bold text-white mb-4">
          We're Currently Under Maintenance
        </h1>
        
        <p className="text-gray-300 mb-8 leading-relaxed">
          Our servers are temporarily unavailable. We're working hard to get everything back online as quickly as possible.
        </p>

        {/* Status Information */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-gray-400">Status</span>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-orange-400 font-medium">
                {isChecking ? 'Checking...' : 'Unavailable'}
              </span>
            </div>
          </div>

          {retryCount > 0 && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400">Retry Attempts</span>
              <span className="text-gray-300">{retryCount}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-gray-400">Next Check</span>
            <span className="text-gray-300">
              {isChecking ? 'Now' : `${timeUntilNextCheck}s`}
            </span>
          </div>
        </div>

        {/* Manual Retry Button */}
        <button
          onClick={onRetry}
          disabled={isChecking}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          {isChecking ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Checking Connection...</span>
            </>
          ) : (
            <>
              <Wifi className="w-5 h-5" />
              <span>Try Again</span>
            </>
          )}
        </button>

        <div className="mt-12 text-gray-500 text-sm">
          <p>Having issues? Contact our support team</p>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
